import React, { useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, { Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { FiEye } from "react-icons/fi";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { Destination } from "../../../../../../../models/destination";
import { Error } from "../../../../../../../models/error";
import { Searches, Filters, Sorts, Sort, Row } from "../../../../../../../models/shipeezi.table";
import DestinationService from "../../../../../../../services/destination.service";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import DestinationModal from "./DestinationModal";
import useStyles from "./styles";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const DestinationPage = ({ permission }: Props) => {
  const { currentUser } = useAppState();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [destination, setDestination] = useState<Destination>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const badgeStatus = (status: string) => (
    <Badge
      className={`badgeComponent ${classes.statusBadge} ${
        status === Status.ACTIVE ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const { refetch, isFetching } = useQuery(
    ["destination.list", page, pageSize, filters, searches, sorting],
    () =>
      DestinationService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting ? JSON.stringify(sorting) : JSON.stringify([["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setDestinations(response.data);
      },
    }
  );

  const { refetch: getDestinationsTotal } = useQuery(
    ["destination.total", filters, searches],
    () =>
      DestinationService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: createDestination, isLoading: isCreating } = useMutation(
    ["create.destination"],
    (destination: Destination) => DestinationService.create(destination),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setHasErrors(false);
        refetch();
        getDestinationsTotal();
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) NotificationToast.error(err?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateDestination, isLoading: isUpdating } = useMutation(
    ["update.destination"],
    ({ id, destination }: { id: number; destination: Destination }) =>
      DestinationService.update(id, destination),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.success.message.destination",
        });
        setHasErrors(false);
        refetch();
      },
      onError: (err: AxiosError<Error>) => {
        if (err && err.response) NotificationToast.error(err?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const editDestination = (index: number) => {
    const findDestination = destinations.find((dest) => Number(dest.id) === index);

    if (findDestination) {
      if (hasErrors !== undefined) setHasErrors(undefined);
      setDestination(findDestination);
      setIsOnViewMode(false);
      setOpenModal(true);
    }
  };

  const changeDestinationStatus = (index: number) => {
    const findDestination = destinations.find((dest) => Number(dest.id) === index);

    if (findDestination) {
      updateDestination({
        id: Number(index),
        destination: {
          ...findDestination,
          status: findDestination.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const viewDestination = (index: number) => {
    const findDestination = destinations.find((dest) => Number(dest.id) === index);

    return (
      <Tooltip title={translate("view") || ""} placement="top">
        <div>
          <FiEye
            className={classes.button}
            size={18}
            onClick={() => {
              if (findDestination) {
                if (hasErrors !== undefined) setHasErrors(undefined);
                setDestination(findDestination);
                setIsOnViewMode(true);
                setOpenModal(true);
              }
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveDestinationSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, destinations);

    setRows([...rowsSelected]);
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="destinationList">
      <ShipeeziNewGrid
        data={destinations}
        columns={[
          { name: "code", title: translate("code") },
          {
            name: "name",
            title: translate("name"),
            formatComponent: (value?: string) => value,
          },
          {
            name: "icon",
            hideSearch: true,
            hideSort: true,
            title: translate("icon"),
          },
          {
            name: "status",
            title: translate("status"),
            hideSearch: true,
            formatComponent: (status: string) => badgeStatus(status),
          },
        ]}
        totalItems={total}
        loadingData={isFetching || isUpdating || isCreating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={clearSelection}
        handleSelect={(rowsArr: number[]) => handleSaveDestinationSelected(rowsArr)}
        rowActions={{
          orderBy: ["handleEdit", "handleView", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (destinationId) => editDestination(Number(destinationId))
            : undefined,
          handleChange: (destinationId) => changeDestinationStatus(Number(destinationId)),
        }}
        rowActionsExtra={[
          {
            title: translate("view"),
            key: "handleView",
            buttonAndAction: viewDestination,
          },
        ]}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleChangeStatus", "handleNewRecord"],
          },
          handleNewRecord: permission && isShowAction(currentUser, permission.create)
            ? {
              text: translate("create"),
              action: () => {
                if (hasErrors !== undefined) setHasErrors(undefined);
                setIsOnViewMode(false);
                setDestination(undefined);
                setOpenModal(true);
              },
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: handleSearch,
          },
          handleFilterBy: {
            categories: [
              { name: translate("code"), column: "code" },
              { name: translate("name"), column: "name" },
              {
                name: translate("status"),
                column: "status",
                options: [Status.ACTIVE, Status.INACTIVE],
              },
            ],
            onChange: handleFilter,
          },
          handleChangeStatus: renderButtonToChangeStatus(),
        }}
      />
      {openModal && (
        <DestinationModal
          open={openModal}
          isOnViewMode={isOnViewMode}
          closeModal={() => setOpenModal(false)}
          destination={destination}
          isLoading={isUpdating || isCreating}
          hasError={hasErrors}
          submit={(destination: Destination) => {
            if (destination.id) updateDestination({ id: destination.id, destination });
            else
              createDestination({
                ...destination,
                companyId: currentUser?.companyId,
              });
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isUpdating}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateDestination({
                id: rowId,
                destination: { status } as Destination,
              });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default DestinationPage;
